import React, { useMemo, useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import './subdepartmentParent.css';

const FALLBACK_SUB_DEPARTMENT_OPTIONS = ['payrool ', 'recruitment'];
const FALLBACK_DEPARTMENT_OPTIONS = ['hr', 'finance'];

export default function SubdepartmentParent({ formFields = [], onChange }) {
  const subDepartmentField = formFields.find((field) => field.fieldLabel === 'Sub-department');
  const departmentField = formFields.find((field) => field.fieldLabel === 'Department');
  const subDepartmentOptions = useMemo(
    () => subDepartmentField?.options || FALLBACK_SUB_DEPARTMENT_OPTIONS,
    [subDepartmentField?.options],
  );
  const departmentOptions = departmentField?.options || FALLBACK_DEPARTMENT_OPTIONS;
  const [parentMappings, setParentMappings] = useState({});
  const [open, setOpen] = useState(false);

  const nextParentMappings = Object.fromEntries(
    Object.entries(parentMappings).filter(([option]) => subDepartmentOptions.includes(option)),
  );
  if (Object.keys(parentMappings).some((option) => !subDepartmentOptions.includes(option))) {
    setParentMappings(nextParentMappings);
  }

  const updateParentMapping = (option, parent) => {
    setParentMappings((currentMappings) => ({
      ...currentMappings,
      [option]: parent,
    }));
  };

const deleteSubDepartment = (optionToDelete) => {
  if (!subDepartmentField) return;

  const updatedFields = formFields.map((field) => {
    if (field !== subDepartmentField) return field;

    return {
      ...field,
      options: field.options.filter(
        (option) => option !== optionToDelete
      ),
    };
  });

  setParentMappings((currentMappings) => {
    const nextMappings = { ...currentMappings };
    delete nextMappings[optionToDelete];
    return nextMappings;
  });

  onChange?.(updatedFields);
};

  return (
    <aside
      className={`subdepartment-parent${open ? ' is-open' : ''}`}
      aria-label={`Options for: ${subDepartmentField?.fieldLabel || 'Sub-department'}`}
    >
      <button
        type="button"
        className="subdepartment-parent__summary"
        aria-expanded={open}
        aria-controls="subdepartment-parent-body"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="subdepartment-parent__summary-text">
          <span className="subdepartment-parent__title">
            Options for: {subDepartmentField?.fieldLabel || 'Sub-department'}
          </span>
          <span className="subdepartment-parent__description">Define options and set parent.</span>
        </span>
        <ChevronDown className="subdepartment-parent__chevron" size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>

      <div id="subdepartment-parent-body" className="subdepartment-parent__body">
      {subDepartmentOptions.length ? (
        <div className="subdepartment-parent__table-wrap">
          <table className="subdepartment-parent__table">
            <thead>
              <tr>
                <th scope="col">Option label</th>
                <th scope="col">Parent</th>
                <th scope="col" aria-label="Delete" />
              </tr>
            </thead>
            <tbody>
              {subDepartmentOptions.map((option) => (
                <tr key={option}>
                  <td>{option}</td>
                  <td>
                    <select
                      aria-label={`Parent for ${option}`}
                      value={parentMappings[option] || ''}
                      disabled={!departmentOptions.length}
                      onChange={(event) => updateParentMapping(option, event.target.value)}
                    >
                      <option value="">
                        {departmentOptions.length ? 'Select parent' : 'No department options'}
                      </option>
                      {departmentOptions.map((department) => (
                        <option key={department} value={department}>
                          {department}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="subdepartment-parent__delete"
                      type="button"
                      aria-label={`Delete ${option}`}
                      title={`Delete ${option}`}
                      onClick={() => deleteSubDepartment(option)}
                    >
                      <Trash2 size={14} strokeWidth={1.5} color="currentColor" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="subdepartment-parent__empty">No options added.</p>
      )}
      </div>
    </aside>
  );
}